import { useEffect, useState } from 'react';
import { Order } from '../../../clients/quipli-service/order/order';
import { Field } from '../../../components/field/field';
import { Button } from '../../../components/form/button';
import { Modal } from '../../../components/modal/modal';
import { Spinner } from '../../../components/spinner/spinner';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../util/toasts';
import { Toggle } from '../../../components/form/toggle';
import { safeRound } from '../../../util/util';

export const CustomSecuritytDeposit = ({
  order,
  onSubmit,
  onClose,
}: {
  order: Order;
  onSubmit: () => void;
  onClose: () => void;
}) => {
  const { setOrderSecurityDeposit } = useAPI();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [percentAmountToggle, setPercentAmountToggle] = useState<'percentage' | 'amount'>(
    order.rentalSecurityPercentage ? 'percentage' : 'amount',
  );
  const [value, setValue] = useState<number>();
  const isDisabled = value === undefined || value < 0;

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const [percentage, amount] = percentAmountToggle === 'percentage' ? [value, undefined] : [undefined, value];
      await setOrderSecurityDeposit(order.id, percentage, amount);

      toast('Security deposit updated.');
      onSubmit();
    } catch (error: any) {
      toast('Something went wrong.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (percentAmountToggle === 'percentage') {
      if (value) {
        setValue(safeRound((100 * value) / order.netCharged, 1));
      } else {
        setValue(order.rentalSecurityPercentage);
      }
    } else {
      if (value) {
        setValue(safeRound((value / 100) * order.netCharged, 2));
      } else {
        setValue(order.rentalSecurity);
      }
    }
  }, [percentAmountToggle]);

  return (
    <Modal
      title="Custom Security Deposit"
      isOpen={true}
      close={handleClose}
      actions={
        isLoading ? (
          <Spinner />
        ) : (
          <>
            <Button className="button-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="button-yellow" onClick={handleSubmit} disabled={isDisabled}>
              Save
            </Button>
          </>
        )
      }
    >
      <div className="flex items-center">
        <div className="mb-1 mr-2">
          <span className="font-sans text-sm text-black-800">Percent/Amount</span>
        </div>
        <Toggle
          onClick={() => setPercentAmountToggle((e) => (e === 'percentage' ? 'amount' : 'percentage'))}
          checked={percentAmountToggle === 'amount'}
        />
      </div>
      <Field
        label={percentAmountToggle === 'percentage' ? 'Percentage of the order (%)' : 'Amount of the deposit'}
        type="number"
        value={value}
        onChange={(newValue) => setValue(newValue)}
      />
    </Modal>
  );
};
